import Immutable from 'immutable';
import {createAction} from 'redux-actions';
import rollbar from '~/vendor/rollbar';
import uuid from 'uuid';
import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';
import {
  setClipartRecoloring,
  setSleevePrintOverride,
  updateLastSaved
} from '~/actions/ConfigActions';
import {fetchFontCategory} from '~/actions/TextActions';
import * as CatalogActions from '~/actions/CatalogActions';
import * as ProductActions from '~/actions/ProductActions';

import {
  getCurrentDesign,
  getIcons,
  getIconsArray,
  hasArtIcon,
  isDirty as isCanvasDirty,
  getCurrentDesignId
} from '~/selectors/CanvasSelectors';
import {
  getCompositeId,
  isDirty as isDesignDirty
} from '~/selectors/DesignSelectors';
import {
  getAnchorStyleId,
  getCurrentColorId,
  getSelectedProductColor,
  getAnchorCompatibleStylesToArray,
  getAddCartItemsToSelectionEligible
} from '~/selectors/ProductSelectors';
import {getStylesArray, getStyle} from '~/selectors/CatalogSelectors';
import CanvasTypes from '~/constants/types/CanvasTypes';
import DesignTypes from '~/constants/types/DesignTypes';
import FontTypes from '~/constants/types/FontTypes';
import ProductTypes from '~/constants/types/ProductTypes';
import ApiConstants from '~/constants/ApiConstants';
import remoteServices from '~/constants/RemoteServices';

import {clipartVersionMapper} from '~/data/deserializers/clipart';
import {
  deserialize,
  hasInvalidUpload
} from '~/data/deserializers/railsFrontend';
import {isArt, isText, isUpload} from '~/data/accessors/icon';
import {isPureMagenta} from '~/data/accessors/color';
import {serialize} from '~/data/serializers/railsFrontend';
import Font from '~/data/records/Font';

import {deviceType} from '~/utils/device_type';
import {buildUrl, updateCompositeIdInUrl} from '~/utils/url';
import {setNaturalDimensionsForArtAndUploads} from '~/data/mutators/icon';
import {isCombineEligible} from '~/utils/combineDesignsEligibility';

import {
  selectCartRecentlyAddedAnchorStyleId,
  selectPreviousProductsDataFromCart,
  selectRecentlyAddedCartItems
} from '~/slices/cartSlice';
import {crop} from './IconActions';

export const clearDesignData = createAction(DesignTypes.CLEAR_DESIGN_DATA);

const saveFailed = createAction(DesignTypes.SAVE_FAILED);

const saveSuccess = createAction(DesignTypes.SAVE_SUCCESS);

export function handleDesignCombination(shouldInitQuantities = true) {
  return (dispatch, getState) => {
    const state = getState();
    const anchorCompatibleStyles = getAnchorCompatibleStylesToArray(state);
    const recentlyAddedCartItems = selectRecentlyAddedCartItems(state);
    const previousProductsData = selectPreviousProductsDataFromCart(state);
    const designId = getCurrentDesignId(state);
    const currentColorId = getCurrentColorId(state);
    const combinedAnchorStyleId = selectCartRecentlyAddedAnchorStyleId(state);
    const combineEligible = isCombineEligible(
      isDesignDirty(state),
      isCanvasDirty(state),
      anchorCompatibleStyles,
      recentlyAddedCartItems
    );
    const addToProducts = getAddCartItemsToSelectionEligible(state)(
      previousProductsData.map(product => product.colorId)
    );

    if (combineEligible) {
      if (addToProducts) {
        dispatch(
          ProductActions.addProduct({
            metricsEnabled: false,
            products: previousProductsData,
            urlPath: '/next/quote',
            isMerging: true
          })
        );
      } else {
        return;
      }
    }
    dispatch({
      type: DesignTypes.SET_COMBINE_DESIGNS,
      payload: {
        isCombined: combineEligible,
        designId,
        currentColorId,
        previousQuoteQuantities: previousProductsData,
        combinedAnchorStyleId,
        shouldInitQuantities
      }
    });
  };
}

export function duplicateDesign() {
  return async (dispatch, getState) => {
    const state = getState();
    const icons = getIcons(state);
    const duplicateIcons = icons.reduce((acc, iconData) => {
      const icon = iconData.set('id', uuid.v4());
      return acc.set(icon.id, icon);
    }, Immutable.OrderedMap());
    const currentDesign = getCurrentDesign(state);
    const duplicatedDesign = currentDesign.merge({
      compositeId: null,
      name: '',
      id: uuid.v4(),
      iconIds: Object.keys(duplicateIcons.toJS())
    });
    dispatch({
      type: DesignTypes.DUPLICATE_DESIGN,
      payload: {
        design: duplicatedDesign,
        icons: duplicateIcons
      }
    });
    const designId = duplicatedDesign.get('id');
    return dispatch({
      type: CanvasTypes.SHOW_DESIGN,
      payload: {designId}
    });
  };
}

export function save(options) {
  return async (dispatch, getState) => {
    return handleSave(dispatch, getState, options);
  };
}

export function autoSave() {
  return async (dispatch, getState) => {
    const state = getState();
    const currentDesign = getCurrentDesign(state);
    const designParams = {
      afterSavePath: '/',
      assistedDesignName: '',
      designId: currentDesign.get('id'),
      designName: currentDesign.get('name'),
      email: currentDesign.get('email'),
      sendEmail: false,
      updating: true,
      verify: null,
      deviceType: deviceType()
    };
    return handleSave(dispatch, getState, designParams);
  };
}

function handleSave(dispatch, getState, designParams) {
  const {
    afterSavePath,
    designName,
    email,
    sendEmail,
    updating,
    verify,
    deviceType,
    addAnchorOnly = false
  } = designParams;
  dispatch({type: DesignTypes.SAVE});

  const state = getState();
  const saveData = serialize(
    state,
    designName,
    email,
    verify,
    sendEmail,
    deviceType,
    addAnchorOnly
  );

  const designIdentifier = getCompositeId(state) || `${email}/${designName}`;

  const queryParams = {api_version: ApiConstants.designs.version};

  const path = `${remoteServices.designs}/api/designs/${
    updating ? designIdentifier : ''
  }`;

  const url = buildUrl(path, queryParams);

  return window
    .fetch(url, {
      method: updating ? 'put' : 'post',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: saveData
    })
    .then(response => {
      return handleSaveResponse(dispatch, state, designParams, response);
    })
    .catch(error => {
      rollbar.critical('Save Failure!', error, {saveData});
      return dispatch(
        saveFailed({
          designName,
          path: afterSavePath,
          error: {
            errors: {
              base: ['server_error'],
              errorMessage: ['Server could not process request.']
            }
          }
        })
      );
    });
}

function handleSaveResponse(dispatch, state, designParams, response) {
  const {
    afterSavePath,
    assistedDesignName,
    designId,
    designName,
    email,
    sendEmail
  } = designParams;

  if (response.ok) {
    return response.json().then(data => {
      if (data.success) {
        updateCompositeIdInUrl(data.design.composite_id);
        const colorId = getCurrentColorId(state);
        return dispatch(
          saveSuccess({
            assistedDesignName,
            categoryName: categoryName(state),
            colorId,
            designId,
            designName,
            email,
            emailSent: sendEmail,
            firstTimeSaving: data.design.first_time_saving,
            hasArtIcon: hasArtIcon(state),
            path: afterSavePath,
            productColor: colorName(state),
            response: data,
            savedDesignID: data.design.designid,
            swapClipartId: swapClipartId(state)
          })
        );
      } else {
        return dispatch(
          saveFailed({
            designId,
            designName,
            path: afterSavePath,
            error: data
          })
        );
      }
    });
  } else {
    return response.json().then(data =>
      dispatch(
        saveFailed({
          designId,
          designName,
          email,
          path: afterSavePath,
          error: data
        })
      )
    );
  }
}

function swapClipartId(state) {
  const icons = getIconsArray(state);
  const swapClipart = icons.find(
    icon => isArt(icon.type) && icon.swappable === true
  );
  return swapClipart ? swapClipart.clipartId : '';
}

function categoryName(state) {
  const anchorStyleId = getAnchorStyleId(state);
  const style = getStyle(state, {styleId: anchorStyleId});
  return style && style.primary_category_name;
}

function colorName(state) {
  const productColor = getSelectedProductColor(state);
  return productColor && productColor.name;
}

const fetchSuccess = createAction(DesignTypes.FETCH_SUCCESS, design => design);

function fetchFailed(error) {
  rollbar.warn('External Service Failure: Fetch Design', error);
  return {
    type: DesignTypes.FETCH_FAILED,
    payload: error,
    error: true
  };
}

export function fetch({
  designId = uuid.v4(),
  cid,
  email,
  designName,
  autoload = true,
  autoSelectProduct = true,
  noUrlOverride = false
}) {
  return async (dispatch, getState) => {
    dispatch({
      type: DesignTypes.FETCH,
      payload: {
        designId: designId
      }
    });

    const state = getState();
    let rawData;
    let isCIGv5;

    try {
      const path = cid || `${email}/${designName}`;
      const response = await loadDesign(path);

      if (!response.ok) {
        throw response;
      }

      rawData = await response.json();

      const apiVersion = Number(response.headers.get('api-version'));
      isCIGv5 = apiVersion === ApiConstants.designs.version;

      if (getSleeveOverride(rawData)) {
        dispatch(setSleevePrintOverride(true));
      }

      if (isCIGv5) {
        dispatch(setClipartRecoloring(getClipartRecoloringOverride(rawData)));
      } else {
        dispatch(setClipartRecoloring(false));
      }
      /* / */
    } catch (error) {
      dispatch(fetchFailed({...error, designId}));
      return;
    }

    if (hasInvalidUpload(rawData)) {
      dispatch(invalidUpload());
    }

    if (autoload) {
      dispatch({type: CanvasTypes.SHOW_DESIGN, payload: {designId}});
    }

    const deserializedData = deserialize({
      ...rawData,
      id: designId,
      apiVersion: isCIGv5
        ? ApiConstants.designs.version
        : ApiConstants.designs.legacyVersion
    });

    dispatch(updateLastSaved(deserializedData.updatedAt));

    deserializedData.icons = await decorateWithClipartVersions(
      deserializedData.icons
    );
    const loadedStyleIds = getStylesArray(state).map(style => style.id);

    const designStyleIds = uniq(
      deserializedData.products.map(product => product.styleId)
    );
    const unloadedStyleIds = designStyleIds.filter(
      styleId => loadedStyleIds.indexOf(styleId) < 0
    );

    if (unloadedStyleIds.length > 0) {
      dispatch(CatalogActions.loadStyles(unloadedStyleIds));
    }

    if (autoSelectProduct) {
      dispatch({
        type: ProductTypes.REPLACE_PRODUCTS,
        payload: deserializedData,
        meta: {noUrlOverride}
      });
    }

    if (deserializedData.icons.size > 0) {
      dispatch({
        type: FontTypes.LOAD_FONT_SEARCH_HISTORY,
        payload: deserializedData.icons
          .filter(icon => isText(icon.type))
          .map(
            icon =>
              new Font({
                displayName: icon.fontName,
                name: icon.font
              })
          )
          .toArray()
      });
    }

    deserializedData['changeProductQuantities'] = autoSelectProduct;

    deserializedData.icons = await setNaturalDimensionsForArtAndUploads(
      deserializedData.icons
    );

    dispatch(fetchSuccess(deserializedData));
    deserializedData.icons.forEach(icon => {
      if (isText(icon.type)) {
        dispatch(fetchFontCategory(icon));
      } else if (isUpload(icon.type)) {
        if (icon.crop) {
          // Crop is not deeply set as a map, and we want to create consistency.
          dispatch(crop(icon, icon.crop, false));
        }
      }
    });
  };
}

async function decorateWithClipartVersions(icons) {
  const promises = Object.values(icons.toJS())
    .filter(icon => isArt(icon.type))
    .map(async icon => {
      try {
        const clipartVersions = await loadClipartVersions(icon.clipartId);
        icons = icons.setIn([icon.id, 'clipartVersions'], clipartVersions);
      } catch (error) {
        rollbar.error('Unable to load clipart versions', error);
      }
    });

  await Promise.all(promises);

  return icons;
}

async function loadClipartVersions(clipartId) {
  const url = `${remoteServices.clipart}/versions/${clipartId}`;
  const response = await window.fetch(url);
  const versions = await response.json();
  const clipartVersions = versions.reduce(clipartVersionMapper, {});

  return clipartVersions;
}

async function loadDesign(path) {
  const designApiVersion = ApiConstants.designs.legacyVersion;

  const queryParams = {
    convert_colors: true,
    translate_uploads: true,
    api_version: designApiVersion,
    show_hidden: false
  };

  const url = buildUrl(
    `${remoteServices.designs}/api/designs/${path}`,
    queryParams
  );

  return window.fetch(url);
}

function invalidUpload() {
  return {
    type: DesignTypes.INVALID_UPLOAD
  };
}

export function shareSuccess(success) {
  return dispatch => {
    dispatch({
      type: DesignTypes.SHARE_SUCCESS,
      payload: {success}
    });
  };
}

export function shareFailure(message) {
  return dispatch => {
    dispatch({
      type: DesignTypes.SHARE_FAILURE,
      payload: {message}
    });
  };
}

export function shareError(message) {
  return dispatch => {
    dispatch({
      type: DesignTypes.SHARE_ERROR,
      payload: {message}
    });
  };
}

function getSleeveOverride(saveData) {
  const sleeveViews = saveData.views.filter(view =>
    [4, 5].includes(view.viewId)
  );
  return sleeveViews.some(view => view.icons.length > 0);
}

function getClipartRecoloringOverride(saveData) {
  // Loop over all artIcons to detect if reColoring has been used
  // Conditions to detect a Color is reColored:
  // If color.original is different from color.user
  // Exception for above condition is
  // if user.transparent is true & color.original isPureMagenta
  let eligibility = false;
  saveData.views.some(view => {
    view.icons.some(icon => {
      if (isArt(icon.type)) {
        icon.colors.some(color => {
          if (!(color.user.transparent && isPureMagenta(color.original))) {
            if (!isEqual(color.original, color.user)) {
              eligibility = true;
            }
          }
          // Break the loop
          if (eligibility) return true;
        });
      }
    });
    // Break the loop
    if (eligibility) return true;
  });
  return eligibility;
}

export const updateInkMatches = inkMatches => (dispatch, getState) => {
  const state = getState();
  const designId = getCurrentDesignId(state);
  dispatch({
    type: DesignTypes.UPDATE_INK_MATCHES,
    payload: {
      designId,
      inkMatches
    }
  });
};

export const removeInkMatch = hexString => (dispatch, getState) => {
  const state = getState();
  const designId = getCurrentDesignId(state);
  dispatch({
    type: DesignTypes.REMOVE_INK_MATCH,
    payload: {
      designId,
      hexString
    }
  });
};

export const resetInkMatches = () => (dispatch, getState) => {
  const state = getState();
  const designId = getCurrentDesignId(state);
  dispatch({
    type: DesignTypes.RESET_INK_MATCHES,
    payload: {
      designId
    }
  });
};
